import { useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Modal } from '../components/Modal';
import { Alert, Spinner } from '../components/ui';
import { formatSwimTimeShort } from '../lib/format';
import type { AdminEvent, ImportPlan } from '../lib/types';

/** Matches the server limit, so an oversized file fails before uploading. */
const MAX_BYTES = 2_000_000;

interface ImportModalProps {
  event: AdminEvent;
  onClose: () => void;
  /** Called after a committed import so the table behind can refresh. */
  onImported: (summary: string) => void;
}

function ActionBadge({ action }: { action: ImportPlan['rows'][number]['action'] }) {
  if (action === 'create') return <span className="badge badge-ok">Create</span>;
  if (action === 'update') return <span className="badge badge-info">Update</span>;
  if (action === 'skipped') return <span className="badge badge-neutral">Example</span>;
  return <span className="badge badge-danger">Skip</span>;
}

/** Column reference, built from the event so the values shown are the real ones. */
function ColumnGuide({ event }: { event: AdminEvent }) {
  const categories = event.categories.map((c) => c.label).join(', ');
  const rows: [string, string, string][] = [
    ['Reference', 'No', 'Blank creates a new entry. Fill it to update that entry instead.'],
    ['First name', 'Yes', ''],
    ['Last name', 'Yes', ''],
    ['Email', 'Yes', 'One per competitor; used to spot duplicates.'],
    ['Date of birth', 'Yes', `YYYY-MM-DD. Minimum age ${event.minAge} on event day.`],
    ['Gender', 'Yes', 'female, male, other or prefer_not_to_say'],
    [
      'Category',
      event.categories.length === 1 ? 'No' : 'Yes',
      event.categories.length === 1
        ? `Defaults to ${categories}.`
        : `One of: ${categories}`,
    ],
    [
      'Predicted time',
      'Yes',
      `mm:ss, between ${formatSwimTimeShort(event.minSeedTimeMs)} and ${formatSwimTimeShort(event.maxSeedTimeMs)}.`,
    ],
    [
      'Emergency contact, Emergency phone',
      event.requireMedicalDeclaration ? 'Yes' : 'No',
      event.requireMedicalDeclaration ? 'Required by this event.' : '',
    ],
    ['Phone, Club, Medical notes, Admin notes', 'No', ''],
    ['Status', 'No', 'Defaults to confirmed.'],
    ['Payment status, Payment reference', 'No', 'Payment status defaults to unpaid.'],
  ];

  return (
    <details className="card card-tight">
      <summary>Which columns can I use?</summary>
      <div className="table-scroll" style={{ marginTop: 'var(--space-3)' }}>
        <table>
          <caption className="visually-hidden">Columns accepted by the importer</caption>
          <thead>
            <tr>
              <th scope="col">Column</th>
              <th scope="col">Required</th>
              <th scope="col">Accepted values</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([column, required, notes]) => (
              <tr key={column}>
                <td>{column}</td>
                <td>{required}</td>
                <td>{notes || <span className="faint">-</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="faint" style={{ marginBottom: 0, marginTop: 'var(--space-3)' }}>
        Column order does not matter and capitalisation is ignored. Extra columns are listed back
        to you and left alone. Spreadsheets rewrite times and dates, so{' '}
        <code>24:30:00</code> and <code>18/07/1990</code> are understood and reported in the
        preview.
      </p>
    </details>
  );
}

export function ImportModal({ event, onClose, onImported }: ImportModalProps) {
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function onPick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setPlan(null);
    setError(null);
    if (!file) {
      setCsv(null);
      setFileName(null);
      return;
    }
    if (file.size > MAX_BYTES) {
      setCsv(null);
      setFileName(null);
      setError(
        `That file is ${(file.size / 1_048_576).toFixed(1)} MB. The limit is 2 MB, ` +
          'so split it and import in batches.',
      );
      return;
    }

    setFileName(file.name);
    setBusy(true);
    try {
      const text = await file.text();
      setCsv(text);
      setPlan(await api.admin.importEntries(text, true));
    } catch (err) {
      setCsv(null);
      setError(err instanceof ApiError ? err.message : 'Could not read that file.');
    } finally {
      setBusy(false);
    }
  }

  async function downloadTemplate() {
    setError(null);
    try {
      await api.admin.importTemplate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not download the template.');
    }
  }

  async function commit() {
    if (!csv) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.admin.importEntries(csv, false);
      const parts = [
        `Created ${result.created ?? 0}`,
        `updated ${result.updated ?? 0}`,
      ];
      if (result.summary.errors > 0) parts.push(`skipped ${result.summary.errors}`);
      if (result.failures && result.failures.length > 0) {
        parts.push(`${result.failures.length} failed to save`);
      }
      onImported(`${parts.join(', ')}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The import could not be completed.');
      setBusy(false);
    }
  }

  const canCommit =
    plan !== null && !busy && plan.summary.create + plan.summary.update > 0;

  return (
    <Modal
      title="Import entries from CSV"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void commit()}
            disabled={!canCommit}
          >
            {busy && plan ? (
              <>
                <Spinner label="Importing" /> Importing...
              </>
            ) : plan ? (
              `Import ${plan.summary.create + plan.summary.update} rows`
            ) : (
              'Import'
            )}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="muted" style={{ marginBottom: 0 }}>
          A row with a <strong>Reference</strong> updates that entry; a row without one creates a
          new entry. Nothing is written until you confirm.
        </p>

        <ColumnGuide event={event} />

        <div className="row-form">
          <div>
            <label className="btn btn-secondary" htmlFor="import-file">
              Choose CSV file
            </label>
            <input
              ref={inputRef}
              id="import-file"
              type="file"
              accept=".csv,text/csv"
              className="visually-hidden"
              onChange={(e) => void onPick(e)}
            />
            {fileName && (
              <span className="faint" style={{ marginLeft: '0.75rem' }}>
                {fileName}
              </span>
            )}
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void downloadTemplate()}
          >
            Download blank template
          </button>
        </div>

        {busy && !plan && <p className="muted">Checking the file...</p>}

        {error && (
          <Alert variant="error" title="Not imported">
            <p style={{ marginBottom: 0 }}>{error}</p>
          </Alert>
        )}

        {plan && (
          <>
            <div className="row">
              <span className="badge badge-ok">{plan.summary.create} to create</span>
              <span className="badge badge-info">{plan.summary.update} to update</span>
              {plan.summary.errors > 0 && (
                <span className="badge badge-danger">{plan.summary.errors} to skip</span>
              )}
              {plan.summary.skipped > 0 && (
                <span className="badge badge-neutral">
                  {plan.summary.skipped} template example{plan.summary.skipped === 1 ? '' : 's'}
                </span>
              )}
              <span className="faint">{plan.summary.total} rows read</span>
            </div>

            {plan.summary.errors > 0 && (
              <Alert variant="warning" title={`${plan.summary.errors} rows will be skipped`}>
                <p style={{ marginBottom: 0 }}>
                  Rows with errors are left out; the rest still import. Fix them in the file and
                  import it again, or correct them by hand afterwards.
                </p>
              </Alert>
            )}

            {plan.capacity.exceeds && (
              <Alert variant="warning" title="This exceeds the entry limit">
                <p style={{ marginBottom: 0 }}>
                  {plan.capacity.active} active now, {plan.capacity.afterImport} after importing,
                  against a limit of {plan.capacity.max}. The import is allowed, but raise the
                  maximum in Settings if that is not intended.
                </p>
              </Alert>
            )}

            {plan.ignoredHeaders.length > 0 && (
              <Alert variant="info" title="Columns not recognised">
                <p style={{ marginBottom: 0 }}>
                  Ignored: {plan.ignoredHeaders.join(', ')}. Check the spelling if one of those was
                  meant to be imported.
                </p>
              </Alert>
            )}

            <div className="table-scroll" style={{ maxHeight: '22rem' }}>
              <table>
                <caption className="visually-hidden">Preview of the rows to be imported</caption>
                <thead>
                  <tr>
                    <th scope="col">Row</th>
                    <th scope="col">Action</th>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Reference</th>
                    <th scope="col">Time</th>
                    <th scope="col">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.rows.map((row) => (
                    <tr key={row.line}>
                      <td className="mono">{row.line}</td>
                      <td>
                        <ActionBadge action={row.action} />
                      </td>
                      <td>{row.name || <span className="faint">-</span>}</td>
                      <td>{row.email || <span className="faint">-</span>}</td>
                      <td className="mono">{row.reference ?? <span className="faint">new</span>}</td>
                      <td className="mono">{row.seedTime}</td>
                      <td>
                        {row.errors.map((message) => (
                          <div key={message} className="field-error">
                            {message}
                          </div>
                        ))}
                        {row.notes.map((note) => (
                          <div key={note} className="faint">
                            {note}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
